import { useState, useCallback, useRef, useEffect } from "react";
import Editor, { type OnMount, type BeforeMount } from "@monaco-editor/react";
import type * as monaco from "monaco-editor";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { EditorToolbar } from "./EditorToolbar";
import { DiffCodeEditor } from "./DiffCodeEditor";
import type { CodeEditorProps } from "../types";

/**
 * CodeEditor — Monaco-based code editor with an integrated toolbar.
 *
 * Baked-in features: Format, Lint, Autocompletion (via injected API props).
 * Exposed features: Save, Sync, Improve with AI, Deploy (via callbacks).
 */
export function CodeEditor({
  // Content
  value: controlledValue,
  defaultValue = "",
  onChange,
  language = "plaintext",
  readOnly = false,
  theme = "vs-dark",

  // File metadata
  fileName,

  // Baked-in APIs
  formatApi,
  lintApi,

  // Exposed callbacks
  onSave,
  onSync,
  onOpenAIPanel,
  onDeploy,
  onLoadIntoChat,
  onMerge,
  onAbandon,
  onToggleViewMain,
  onToggleViewGithub,

  // External markers (e.g. static analysis)
  externalMarkers,

  // Coverage line decorations (green = covered, red = uncovered)
  coverageLines,

  // State indicators
  isLoadingContent = false,
  isDirty = false,
  isSaving = false,
  isSyncing = false,
  isEdited = false,
  editedFilesCount = 0,
  isDeploying = false,
  deploymentMessage,
  isAIPanelOpen = false,
  hasAnyActivityRunning = false,
  branchName,
  viewingMain = false,
  isMerging = false,
  isAbandoning = false,
  viewingGithub = false,
  isLoadingGithub = false,

  // Toolbar
  showToolbar = true,

  // Diff mode (when set, renders DiffEditor instead of Editor)
  diff,
  diffViewMode = "inline",

  // Monaco options override
  editorOptions,

  // Styling
  className,
}: CodeEditorProps) {
  // ── Diff mode: render DiffCodeEditor (no toolbar) ──
  if (diff) {
    return (
      <DiffCodeEditor
        original={diff.original}
        modified={diff.modified}
        language={language}
        theme={theme}
        viewMode={diffViewMode}
        readOnly={readOnly}
        editorOptions={editorOptions}
        className={className}
      />
    );
  }

  // ── Coverage CSS (injected once) ──
  useEffect(() => {
    if (document.getElementById("coverage-styles")) return;
    const style = document.createElement("style");
    style.id = "coverage-styles";
    style.textContent = `
      .coverage-line-hit { background: rgba(34,197,94,0.12) !important; }
      .coverage-line-miss { background: rgba(239,68,68,0.12) !important; }
      .coverage-glyph-hit { background: #22c55e; width: 4px !important; margin-left: 3px; border-radius: 2px; }
      .coverage-glyph-miss { background: #ef4444; width: 4px !important; margin-left: 3px; border-radius: 2px; }
    `;
    document.head.appendChild(style);
  }, []);

  // ── Internal state ──
  const [isFormatting, setIsFormatting] = useState(false);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof monaco | null>(null);

  // ── Get current value ──
  const getEditorValue = useCallback((): string => {
    return editorRef.current?.getValue() ?? controlledValue ?? defaultValue;
  }, [controlledValue, defaultValue]);

  // ── Format handler (baked-in) ──
  const handleFormat = useCallback(async () => {
    if (!formatApi || isFormatting) return;

    const code = getEditorValue();
    if (!code) return;

    setIsFormatting(true);
    try {
      const formatted = await formatApi.format(code, language);
      if (formatted !== code) {
        // Update editor content
        editorRef.current?.setValue(formatted);
        onChange?.(formatted);
      }
    } catch (error) {
      console.error("[CodeEditor] Format failed:", error);
    } finally {
      setIsFormatting(false);
    }
  }, [formatApi, isFormatting, getEditorValue, language, onChange]);

  // ── Lint integration (baked-in) ──
  const runLint = useCallback(
    async (code: string) => {
      if (!lintApi || !monacoRef.current || !editorRef.current || !fileName)
        return;

      try {
        const results = await lintApi.lint(code, fileName);
        const model = editorRef.current.getModel();
        if (!model) return;

        const monacoInstance = monacoRef.current;

        const markers: monaco.editor.IMarkerData[] = results.map((r) => ({
          startLineNumber: r.line,
          startColumn: r.column,
          endLineNumber: r.endLine ?? r.line,
          endColumn: r.endColumn ?? r.column + 1,
          message: r.message,
          severity:
            r.severity === "error"
              ? monacoInstance.MarkerSeverity.Error
              : r.severity === "warning"
                ? monacoInstance.MarkerSeverity.Warning
                : r.severity === "info"
                  ? monacoInstance.MarkerSeverity.Info
                  : monacoInstance.MarkerSeverity.Hint,
        }));

        monacoInstance.editor.setModelMarkers(
          model,
          "ncoder-lint",
          markers,
        );
      } catch (error) {
        console.error("[CodeEditor] Lint failed:", error);
      }
    },
    [lintApi, fileName],
  );

  // ── Apply external markers (e.g. static analysis) ──
  useEffect(() => {
    const editor = editorRef.current;
    const monacoInstance = monacoRef.current;
    const model = editor?.getModel();
    if (!model || !monacoInstance || !externalMarkers?.length) {
      if (editor && monacoInstance) {
        const m = editor.getModel();
        if (m) monacoInstance.editor.setModelMarkers(m, "ncoder-external", []);
      }
      return;
    }
    const markerData: monaco.editor.IMarkerData[] = externalMarkers.map(
      (r) => ({
        startLineNumber: r.line,
        startColumn: r.column,
        endLineNumber: r.endLine ?? r.line,
        endColumn: r.endColumn ?? r.column + 1,
        message: r.message,
        severity:
          r.severity === "error"
            ? monacoInstance.MarkerSeverity.Error
            : r.severity === "warning"
              ? monacoInstance.MarkerSeverity.Warning
              : r.severity === "info"
                ? monacoInstance.MarkerSeverity.Info
                : monacoInstance.MarkerSeverity.Hint,
      }),
    );
    monacoInstance.editor.setModelMarkers(model, "ncoder-external", markerData);
  }, [externalMarkers]);

  // ── Coverage line decorations (green/red backgrounds) ──
  const coverageDecorationsRef = useRef<string[]>([]);
  const [editorReady, setEditorReady] = useState(false);
  useEffect(() => {
    const editor = editorRef.current;
    const monacoInst = monacoRef.current;
    if (!editor || !monacoInst || !editorReady) return;
    if (!coverageLines?.covered_lines?.length && !coverageLines?.uncovered_lines?.length) {
      if (coverageDecorationsRef.current.length) {
        coverageDecorationsRef.current = editor.deltaDecorations(coverageDecorationsRef.current, []);
      }
      return;
    }
    const decorations: monaco.editor.IModelDeltaDecoration[] = [];
    for (const line of (coverageLines.covered_lines || [])) {
      decorations.push({ range: new monacoInst.Range(line, 1, line, 1), options: {
        isWholeLine: true, className: "coverage-line-hit", glyphMarginClassName: "coverage-glyph-hit",
        overviewRuler: { color: "rgba(34,197,94,0.6)", position: 1 },
      }});
    }
    for (const line of (coverageLines.uncovered_lines || [])) {
      decorations.push({ range: new monacoInst.Range(line, 1, line, 1), options: {
        isWholeLine: true, className: "coverage-line-miss", glyphMarginClassName: "coverage-glyph-miss",
        overviewRuler: { color: "rgba(239,68,68,0.6)", position: 1 },
      }});
    }
    coverageDecorationsRef.current = editor.deltaDecorations(coverageDecorationsRef.current, decorations);
  }, [coverageLines, editorReady]);

  // ── Register Apex language synchronously before editor mounts ──
  const handleBeforeMount = useCallback((monacoInstance: typeof monaco) => {
    const { registerApexWithInstance } = require("../registerApexLanguage");
    registerApexWithInstance(monacoInstance);
  }, []);

  // ── Editor mount ──
  const handleEditorMount: OnMount = useCallback(
    (editor, monacoInstance) => {
      editorRef.current = editor;
      monacoRef.current = monacoInstance;
      setEditorReady(true);

      // Run initial lint
      if (lintApi) {
        const code = editor.getValue();
        if (code) runLint(code);
      }
    },
    [lintApi, runLint],
  );

  // ── Content change handler ──
  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) {
        onChange?.(value);

        // Debounced lint on change
        if (lintApi) {
          runLint(value);
        }
      }
    },
    [onChange, lintApi, runLint],
  );

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl+S → Save
      if (isMod && e.key === "s" && !e.shiftKey && !e.altKey) {
        if (onSave && isDirty) {
          e.preventDefault();
          onSave();
        }
      }

      // Shift+Alt+F → Format
      if (e.shiftKey && e.altKey && e.key === "f") {
        if (formatApi) {
          e.preventDefault();
          handleFormat();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSave, isDirty, formatApi, handleFormat]);

  // ── Merged Monaco options ──
  const mergedOptions: Record<string, unknown> = {
    minimap: { enabled: true },
    fontSize: 14,
    lineNumbers: "on",
    wordWrap: "on",
    automaticLayout: true,
    scrollBeyondLastLine: false,
    glyphMargin: true,
    renderLineHighlight: "all",
    showFoldingControls: "always",
    readOnly,
    tabSize: 2,
    folding: true,
    bracketPairColorization: { enabled: true },
    guides: {
      indentation: true,
      bracketPairs: true,
    },
    ...editorOptions,
  };

  return (
    <div className={cn("h-full flex flex-col bg-background", className)}>
      {/* Toolbar */}
      {showToolbar && (
        <EditorToolbar
          isDirty={isDirty}
          isSaving={isSaving}
          isSyncing={isSyncing}
          isEdited={isEdited}
          editedFilesCount={editedFilesCount}
          isDeploying={isDeploying}
          deploymentMessage={deploymentMessage}
          isFormatting={isFormatting}
          isAIPanelOpen={isAIPanelOpen}
          hasAnyActivityRunning={hasAnyActivityRunning}
          onSave={onSave}
          onSync={onSync}
          onFormat={formatApi ? handleFormat : undefined}
          onDeploy={onDeploy}
          onOpenAIPanel={onOpenAIPanel}
          onLoadIntoChat={onLoadIntoChat}
          onMerge={onMerge}
          onAbandon={onAbandon}
          onToggleViewMain={onToggleViewMain}
          onToggleViewGithub={onToggleViewGithub}
          branchName={branchName}
          viewingMain={viewingMain}
          isMerging={isMerging}
          isAbandoning={isAbandoning}
          viewingGithub={viewingGithub}
          isLoadingGithub={isLoadingGithub}
        />
      )}

      {/* Editor or content loading state */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {isLoadingContent ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Loading file...</p>
            </div>
          </div>
        ) : (
        <Editor
          height="100%"
          path={fileName || undefined}
          language={language}
          theme={theme}
          value={controlledValue}
          defaultValue={defaultValue}
          onChange={handleEditorChange}
          beforeMount={handleBeforeMount}
          onMount={handleEditorMount}
          options={mergedOptions}
          loading={
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin" />
                <p className="text-sm">Loading editor...</p>
              </div>
            </div>
          }
        />
        )}
      </div>
    </div>
  );
}

